import { HierarchyNodeType, MayBeNull } from '@wpp-open/core'

import { LOCAL_APP_CODE, LOCAL_APP_NAME, LOCAL_LEGACY_APP_CODE, LOCAL_LEGACY_CONTAINER_ID } from 'constants/apps'
import { Permission } from 'constants/permission'
import { LegacyAppWindowLibraryName } from 'legacy/types/apps'
import { BaseAppConfig } from 'types/apps/apps'

export enum MicroAppType {
  System = 'System',
  Native = 'Native',
  Legacy = 'Legacy',
  Local = 'Local',
  LocalLegacy = 'LocalLegacy',
}

export enum MicroAppLibraryType {
  Window = 'WINDOW',
  SystemJS = 'SYSTEM_JS',
  ESM = 'ESM',
}

export enum SystemAppCode {
  DevHub = 'SYSTEM_DEVHUB',
  Marketplace = 'SYSTEM_MARKETPLACE',
  Orchestration = 'SYSTEM_ORCHESTRATION',
  News = 'SYSTEM_NEWS',
  ThemeBuilder = 'SYSTEM_THEME_BUILDER',
  MasterData = 'SYSTEM_MASTER_DATA',
  AiSettings = 'SYSTEM_AI_SETTINGS',
  ProcessBuilder = 'SYSTEM_PROCESS_BUILDER',
}

export type MicroAppConfig =
  | SystemMicroAppConfig
  | NativeMicroAppConfig
  | LocalMicroAppConfig
  | LegacyMicroAppConfig
  | LocalLegacyMicroAppConfig

interface BaseMicroAppConfig extends BaseAppConfig {
  osRoute: string
  libraryType: MicroAppLibraryType
  windowLibraryName: MayBeNull<string>
  bundleUrl: string
  permission: MayBeNull<string>
  /**
   * Whether this app should always be launched in a project
   */
  isProjectOnly: boolean
  /**
   * List of hierarchy node types that should be
   * present in the selected hierarchy tree when the app is launched.
   *
   * Example: [Client, Brand] means that the app should have both
   * Client and Brand levels somewhere in the selected hierarchy node tree, in any order.
   */
  requiredHierarchy: HierarchyNodeType[]
}

export interface SystemMicroAppConfig extends BaseMicroAppConfig {
  type: MicroAppType.System
  stableId: SystemAppCode
  libraryType: Exclude<MicroAppLibraryType, MicroAppLibraryType.Window>
  windowLibraryName: null
  permission: MayBeNull<Permission>
  isProjectOnly: false
  requiredHierarchy: never[]
}

export interface NativeMicroAppConfig extends BaseMicroAppConfig {
  type: MicroAppType.Native
}

export interface LocalMicroAppConfig extends BaseMicroAppConfig {
  type: MicroAppType.Local
  stableId: typeof LOCAL_APP_CODE
  name: typeof LOCAL_APP_NAME
  permission: null
  isProjectOnly: false
  requiredHierarchy: never[]
}

interface BaseLegacyMicroAppConfig extends BaseMicroAppConfig {
  libraryType: MicroAppLibraryType.Window
  windowLibraryName: string
  containerId: string
}

export interface LegacyMicroAppConfig extends BaseLegacyMicroAppConfig {
  type: MicroAppType.Legacy
  windowLibraryName: LegacyAppWindowLibraryName
}

export interface LocalLegacyMicroAppConfig extends BaseLegacyMicroAppConfig {
  type: MicroAppType.LocalLegacy
  stableId: typeof LOCAL_LEGACY_APP_CODE
  name: typeof LOCAL_APP_NAME
  permission: null
  isProjectOnly: false
  requiredHierarchy: never[]
  containerId: typeof LOCAL_LEGACY_CONTAINER_ID
}
